import Phaser from 'phaser';

import { createGameConfig } from './game-config';
import { formatPauseAnnouncement } from './i18n/dynamic-labels';
import { RESOURCE_KEYS, translate, type Locale, type ResourceKey } from './i18n/resources';
import type { GameAction, MovementDirection } from './input/input-contract';
import { LOCAL_STATE, type LocalSettings } from './persistence/local-state';
import { openResetConfirmation } from './persistence/reset-confirmation';
import {
  keyboardBindingsForScheme,
  SETTINGS,
} from './presentation/accessibility-settings';
import { GAME_RUNTIME } from './runtime/game-runtime';
import { createQaArenaResult, isArenaQaEnabled, type QaArenaScenario } from './qa/arena-qa';
import { SCENE_KEYS } from './presentation/scene-contract';
import './styles.css';

const language = document.querySelector<HTMLSelectElement>('#language');
const highContrast = document.querySelector<HTMLInputElement>('#high-contrast');
const reducedMotion = document.querySelector<HTMLInputElement>('#reduced-motion');
const largeText = document.querySelector<HTMLInputElement>('#large-text');
const captions = document.querySelector<HTMLInputElement>('#captions');
const audioMuted = document.querySelector<HTMLInputElement>('#audio-muted');
const masterVolume = document.querySelector<HTMLInputElement>('#master-volume');
const musicVolume = document.querySelector<HTMLInputElement>('#music-volume');
const effectsVolume = document.querySelector<HTMLInputElement>('#effects-volume');
const controlScheme = document.querySelector<HTMLSelectElement>('#control-scheme');
const settingsForm = document.querySelector<HTMLFormElement>('#local-settings-form');
const settingsStatus = document.querySelector<HTMLElement>('#settings-status');
const persistenceStatus = document.querySelector<HTMLElement>('#persistence-status');
const progressStatus = document.querySelector<HTMLElement>('#progress-status');
const resetDialog = document.querySelector<HTMLDialogElement>('#reset-progress-dialog');
const openReset = document.querySelector<HTMLButtonElement>('#open-reset-progress');
const status = document.querySelector<HTMLElement>('#game-status');
const soundCaption = document.querySelector<HTMLElement>('#sound-caption');
const qaControls = document.querySelector<HTMLElement>('#arena-qa-controls');

const asResourceKey = (candidate: string): ResourceKey => {
  if (!RESOURCE_KEYS.includes(candidate as ResourceKey)) {
    throw new Error(`Unknown translation key: ${candidate}`);
  }
  return candidate as ResourceKey;
};

const localizeShell = (locale: Locale): void => {
  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const key = element.dataset.i18n;
    if (key !== undefined) element.textContent = translate(locale, asResourceKey(key));
  }
  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n-aria-label]')) {
    const key = element.dataset.i18nAriaLabel;
    if (key !== undefined) element.setAttribute('aria-label', translate(locale, asResourceKey(key)));
  }
};

const setRange = (input: HTMLInputElement | null, value: number): void => {
  if (input === null) return;
  input.value = String(value);
  const output = document.querySelector<HTMLOutputElement>(`#${input.id}-output`);
  if (output !== null) output.value = `${value}%`;
};

const applySettings = (settings: LocalSettings): void => {
  document.documentElement.lang = settings.language;
  document.documentElement.toggleAttribute('data-high-contrast', settings.accessibility.highContrast);
  document.documentElement.toggleAttribute('data-reduced-motion', settings.accessibility.reducedMotion);
  document.documentElement.toggleAttribute('data-large-text', settings.accessibility.largeText);
  document.documentElement.toggleAttribute('data-captions', settings.accessibility.captions);
  document.documentElement.toggleAttribute('data-audio-muted', settings.audio.muted);
  if (!settings.accessibility.captions && soundCaption !== null) soundCaption.textContent = '';
  localizeShell(settings.language);
  GAME_RUNTIME.keyboard.setBindings(keyboardBindingsForScheme(settings.controlScheme));
  if (language !== null) language.value = settings.language;
  if (highContrast !== null) highContrast.checked = settings.accessibility.highContrast;
  if (reducedMotion !== null) reducedMotion.checked = settings.accessibility.reducedMotion;
  if (largeText !== null) largeText.checked = settings.accessibility.largeText;
  if (captions !== null) captions.checked = settings.accessibility.captions;
  if (audioMuted !== null) audioMuted.checked = settings.audio.muted;
  if (controlScheme !== null) controlScheme.value = settings.controlScheme;
  setRange(masterVolume, settings.audio.masterVolume);
  setRange(musicVolume, settings.audio.musicVolume);
  setRange(effectsVolume, settings.audio.effectsVolume);
};

const renderLocalState = (): void => {
  const current = SETTINGS.load();
  const progress = LOCAL_STATE.loadProgress();
  if (progressStatus !== null) {
    progressStatus.textContent = translate(current.language, 'status.unlocked', {
      levels: progress.unlockedLevelIds.join(', '),
    });
  }
  if (persistenceStatus !== null) {
    persistenceStatus.textContent = translate(
      current.language,
      LOCAL_STATE.persistenceStatus() === 'available'
        ? 'status.storageAvailable'
        : 'status.storageUnavailable',
    );
  }
};

const initialSettings = SETTINGS.load();
applySettings(initialSettings);
if (status !== null) status.textContent = translate(initialSettings.language, 'status.loading');
renderLocalState();
const detachRuntime = GAME_RUNTIME.attach(window, document);
const game = new Phaser.Game(createGameConfig('game'));

if (isArenaQaEnabled(window.location) && qaControls !== null) {
  qaControls.hidden = false;
  qaControls.addEventListener('click', (event) => {
    const scenario = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-qa-arena]')?.dataset.qaArena;
    if (scenario === 'win' || scenario === 'health' || scenario === 'time') {
      game.scene.start(SCENE_KEYS.results, createQaArenaResult(scenario as QaArenaScenario));
    }
  });
}

const unsubscribeSettings = SETTINGS.subscribe((settings) => {
  applySettings(settings);
  renderLocalState();
});
const unsubscribePause = GAME_RUNTIME.pause.subscribe((paused, reasons) => {
  if (status !== null) {
    const locale = SETTINGS.load().language;
    status.textContent = paused
      ? formatPauseAnnouncement(locale, reasons)
      : translate(locale, 'status.resumed');
  }
});

settingsForm?.addEventListener('change', () => {
  if (
    language === null ||
    highContrast === null ||
    reducedMotion === null ||
    largeText === null ||
    captions === null ||
    audioMuted === null ||
    masterVolume === null ||
    musicVolume === null ||
    effectsVolume === null ||
    controlScheme === null
  ) {
    return;
  }
  const settings: LocalSettings = {
    version: 1,
    language: language.value === 'ru' ? 'ru' : 'en',
    controlScheme: controlScheme.value === 'ijkl' ? 'ijkl' : 'wasd',
    accessibility: {
      highContrast: highContrast.checked,
      reducedMotion: reducedMotion.checked,
      largeText: largeText.checked,
      captions: captions.checked,
    },
    audio: {
      muted: audioMuted.checked,
      masterVolume: Number(masterVolume.value),
      musicVolume: Number(musicVolume.value),
      effectsVolume: Number(effectsVolume.value),
    },
  };
  const saved = SETTINGS.save(settings);
  if (settingsStatus !== null) {
    settingsStatus.textContent = translate(saved.language, 'settings.saved');
  }
});

openReset?.addEventListener('click', () => {
  if (resetDialog !== null) openResetConfirmation(resetDialog);
});
resetDialog?.addEventListener('close', () => {
  LOCAL_STATE.confirmProgressReset(resetDialog.returnValue === 'reset');
  renderLocalState();
});

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-direction]')) {
  const direction = button.dataset.direction as MovementDirection;
  const release = (): void => GAME_RUNTIME.touch.setDirection(direction, false);
  button.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    button.setPointerCapture(event.pointerId);
    GAME_RUNTIME.touch.setDirection(direction, true);
  });
  button.addEventListener('pointerup', release);
  button.addEventListener('pointercancel', release);
  button.addEventListener('lostpointercapture', release);
  button.addEventListener('click', (event) => {
    if (event.detail !== 0) return;
    GAME_RUNTIME.touch.setDirection(direction, true);
    window.setTimeout(release, 160);
  });
}

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-action]')) {
  const action = button.dataset.action as GameAction;
  button.addEventListener('click', () => GAME_RUNTIME.touch.activate(action));
}

if (status !== null) status.textContent = translate(initialSettings.language, 'status.ready');

window.addEventListener(
  'beforeunload',
  () => {
    unsubscribeSettings();
    unsubscribePause();
    detachRuntime();
    game.destroy(true);
  },
  { once: true },
);
